import React, { useMemo, useState, useEffect } from 'react';
import {
  getPlanInfo,
  mapPlanAllotmentOptions,
} from '@mm/state/helpers/billing-info-helpers';
import { EnhancedBillingPage } from '@mm/state/interfaces/connected-page';
import {
  PlansAllotments,
  EnhancedPlan,
} from '@mm/state/interfaces/subscription';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../../common/components/PrimarySelect/PrimarySelect';
import '../BillingPageCard.pcss';

type Props = {
  page: EnhancedBillingPage;
  setLevel: (level: number) => void;
  setOptionsToShow: (yes: boolean) => void;
  plansAllotments: PlansAllotments;
  plan: EnhancedPlan[];
};

export const ExtraSendsSelect = (props: Props) => {
  const { page, setLevel, setOptionsToShow, plansAllotments, plan } = props;
  const currentSubscription = page?.subscription;
  const planInfoMemoised = useMemo(() => {
    return getPlanInfo(page, page.pageBillingType);
  }, [page, page.pageBillingType]);

  const currentSendsLevel = Number(currentSubscription?.sendAllotmentLevel);

  const { isProUnicorn, isProPlan, isAnyTeamPlan, isFreePlan, isInstachamp } =
    planInfoMemoised;

  const plansAllotmentsOptions = mapPlanAllotmentOptions({
    plansAllotments,
    getExtraAllotment: false,
    currentSendsLevel,
    page,
    plan,
    useFiltered: true,
  });

  useEffect(() => {
    if (plansAllotmentsOptions) {
      setLevel(plansAllotmentsOptions[0].level);
    }
  }, []);

  useEffect(() => {
    if (plansAllotmentsOptions.length > 0) {
      setOptionsToShow(true);
    } else {
      setOptionsToShow(false);
    }
  }, [plansAllotmentsOptions]);

  const [selectedLevel, setSelectedLevel] = useState(plansAllotmentsOptions[0]);

  const isOneOfSupportedPlans =
    isProUnicorn || isProPlan || isAnyTeamPlan || isInstachamp;

  if (
    isFreePlan ||
    !isOneOfSupportedPlans ||
    !plansAllotmentsOptions.length ||
    !page.subscription?.endsAt
  ) {
    return null;
  }

  const onDropdownItemChange = (plan: {
    id: number;
    name: string;
    level: number;
  }) => {
    setSelectedLevel(plan);
    setLevel(plan.level);
  };

  const getFormatedPriceAndName = (option: DropdownOption) => {
    const { subscription } = page || {};
    const { level, name } = option;
    const price = level * (subscription?.sendAllotmentAddOnPrice || 0);
    const formattedName = name?.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    return { price, formattedName };
  };

  const customOptionRenderer = (option: DropdownOption) => {
    const { subscription } = page || {};
    const { price, formattedName } = getFormatedPriceAndName(option);
    const billingPeriod = subscription?.billingPeriodLength;
    const customPeriodLength = subscription?.planIntervalLength;

    const period =
      billingPeriod === 'annually'
        ? 'yearly'
        : billingPeriod === 'monthly' && (customPeriodLength || 0) > 1
        ? `${customPeriodLength} months`
        : 'monthly';

    return (
      <span>
        {subscription && (
          <span className="billing-page-card__select--number">
            <strong>${price}</strong>
          </span>
        )}
        {subscription && ' '}
        <span className="billing-page-card__select">
          {formattedName} credits/{period}{' '}
        </span>
      </span>
    );
  };

  return (
    <div className="active-plan-details__select-wrapper">
      <PrimarySelect
        showSearchFilter={false}
        className="extra-sends-primary-selector"
        placeholder="Choose a plan"
        onChange={(plan: { id: number; name: string; level: any }) =>
          onDropdownItemChange(plan)
        }
        options={plansAllotmentsOptions}
        value={selectedLevel}
        customOptionRenderer={customOptionRenderer}
      />
    </div>
  );
};
